import React, { useState, useEffect, useRef } from 'react';
import Silk from '../components/Silk';
import './MarketOverview.css';

// --- EXISTING IMPORTS (unchanged) ---
import { Header } from '../components/Header';
import { ModernSearchBar } from '../components/ModernSearchBar';
import { useLayoutMaster } from '../utils/useLayoutMaster';
import { ClickableAssetCard } from '../components/AssetLink';

import { MagmaNewsSection } from '../components/MagmaNewsSection';

import FreeShopIndicator from '../components/dutyfree_indicator';
import TravelFXCard from '../components/travelcard';
import { PolymarketCard } from '../components/polymarkets_card';

import { CryptoTreemap } from '../components/treemap_crypto';
import { CurvaDeJuros } from '../components/CurvaDeJuros';
import { SiscomexGenoma } from '../components/SiscomexGenoma';
import { ThesisCenariosBlock } from '../components/ThesisCenariosBlock';
import HypothesisExplorer from '../components/HypothesisExplorer';
import HypothesisCorrelationLab from '../components/HypothesisCorrelationLab';

import { IndexFamilyRow } from '../components/IndexFamilyRow';
import { IndexCorrelationLab } from '../components/IndexCorrelationLab';
import { IndexTopHoldings } from '../components/IndexTopHoldings';
import { IndexHeroBanner } from '../components/IndexHeroBanner';
import GenomaTradeExplorer from "../components/GenomaTradeExplorer";
import GenomaTeses from "../components/GenomaTeses";
import GenomaTesesSidebar from "../components/GenomaTesesSidebar";

import { BitcoinGlobalScaleCard } from '../components/BitcoinGlobalScaleCard';
import { COPOMOptionsCard } from '../components/COPOMOptionsCard';
import { InvestmentTheme } from '../components/InvestmentTheme';
import { AssetCard, AssetCardWithTickers } from '../components/AssetCard';
import { CurrencyClusters, IndexClusters } from '../components/SmartClusters';
import { CommoditiesCard, CurrenciesCard, IndicesCard } from '../components/MarketDataCards';

import { AlertTeaser } from '../components/AlertTeaser';



import { NewsCardsSection } from '../components/NewsCards_Real';
import { EarningsCalendarCard, EconomicCalendarCard } from '../components/CalendarComponents';
import { CryptoCardsSection } from '../components/CryptoCards';
import { MoedasCard, MetaisCard } from '../components/MoedasMetaisCards';
import { SiscomexCard, SelicCard } from '../components/SiscomexSelicCards';
import { CommodityClusters } from '../components/CommodityClusters';
import { ELECTION_CLUSTERS, GLOBAL_MACRO_THESES } from '../data/thesisData';
import RRAlertCard from '../components/RRAlertCard';
import { RRTVPlayer } from './RRTVPlayer';

import { MagmaHeroSection } from '../components/MagmaHeroSection';
import magmaLogo from '../assets/logos/LOGO RR MAGMA.svg';

const API_URL = process.env.REACT_APP_API_URL || '/api/go';


// ─────────────────────────────────────────────────────────────
// SECTION DIVIDER
// ─────────────────────────────────────────────────────────────

function SectionDivider({ number, title, badge, badgeColor = '#D4AF37', desc, href, hrefLabel }) {
  return (
    <div style={{ margin: '3.5rem 0 1.8rem', position: 'relative' }}>
      {/* Purple left accent line */}
      <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 3, background: 'linear-gradient(180deg,#6660fe,rgba(102,96,254,0))', borderRadius: 2 }} />
      <div style={{ paddingLeft: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
          {number && (
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, color: 'rgba(102,96,254,0.6)', letterSpacing: '0.18em' }}>{number}</span>
          )}
          <h2 style={{ fontFamily: "'Roble',system-ui,sans-serif", fontSize: '1.9rem', fontWeight: 800, color: 'rgba(255,255,255,0.92)', margin: 0, letterSpacing: '-0.02em', lineHeight: 1 }}>
            {title}
          </h2>
          {badge && (
            <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', background: `${badgeColor}18`, color: badgeColor, border: `1px solid ${badgeColor}40`, padding: '3px 10px', borderRadius: 100 }}>
              {badge}
            </span>
          )}
          {href && (
            <a href={href} style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", marginLeft: 'auto', color: 'rgba(255,255,255,0.35)', textDecoration: 'none', fontSize: '0.8rem', fontWeight: 400, borderBottom: '1px solid rgba(255,255,255,0.15)', paddingBottom: 1 }}>
              {hrefLabel || 'Ver mais'} ↗
            </a>
          )}
        </div>
        {desc && (
          <p style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, color: 'rgba(255,255,255,0.32)', fontSize: '0.82rem', maxWidth: 680, lineHeight: 1.6, margin: 0 }}>{desc}</p>
        )}
      </div>
      {/* Separator line */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg,rgba(102,96,254,0.3),rgba(255,255,255,0.06) 40%,transparent)' }} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────

function MarketOverviewPage() {
  const layout = useLayoutMaster();
  const [isDark, setIsDark] = useState(true);
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);

  const toggleTheme = () => setIsDark(!isDark);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/overview`);
      const data = await response.json();
      setMarketData(data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setLoading(false);
    }
  };

  const theme = {
    background:    'rgba(18,18,18,0.8)',
    textPrimary:   '#fff',
    textSecondary: '#999',
    border:        'rgba(212,175,55,0.2)',
    borderHover:   'rgba(212,175,55,0.6)',
    cardBg:        'rgba(18,18,18,0.8)',
    heroBg:        'linear-gradient(135deg,rgba(102,96,254,0.15),rgba(61,58,153,0.1))',
    itemBg:        'rgba(0,0,0,0.3)',
    itemBgHover:   'rgba(212,175,55,0.1)',
    tickerBg:      'rgba(0,0,0,0.4)',
  };

  const getTopTickers = () => {
    if (!marketData) return [];
    return [...(marketData.topGainers || []).slice(0, 5), ...(marketData.topLosers || []).slice(0, 5)];
  };

  return (
    <div className={`app-container ${isDark ? 'dark' : 'light'}`}>

      {/* Silk background */}
      <div className="silk-background">
        <Silk speed={31} scale={1.5} color='#6660fe' noiseIntensity={2} rotation={0.2} />
      </div>

      {/* ── IDENTITY BANNER ── */}
      <MagmaHeroSection marketData={marketData} />

      {/* ── HEADER ── */}
      <Header
        theme={theme}
        isDark={isDark}
        toggleTheme={toggleTheme}
        topTickers={getTopTickers()}
        marketData={marketData}
      />

      <div className="content-wrapper" style={{ minHeight: '100vh' }}>
        <main style={layout.container()}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '4rem', fontFamily: "'IBM Plex Mono',monospace" }}>
              <div style={{ color: '#D4AF37', fontSize: '1.1rem', letterSpacing: '0.12em' }}>CARREGANDO DADOS...</div>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>

              {/* ── GENOMA ── */}
              <SectionDivider number="05" title="DNA do Mercado" badge="" badgeColor="#9333ea" desc="Análise preditiva com base no fluxo de transações do comércio exterior brasileiro." href="http://100.82.243.76:8060/" hrefLabel="Plataforma Genoma" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(350px,1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
                <div id="genoma-override">
                  <GenomaTradeExplorer/>
                  {/* <GenomaTeses /> */}
                  <style>{`#genoma-override>div{grid-template-columns:1fr!important}@media(min-width:768px){#genoma-override>div{grid-template-columns:repeat(2,1fr)!important}}`}</style>
                </div>
              </div>

              {/* NEW — alerts full width, then RR TV full width below */}
              <div style={{ width: '100%', marginBottom: '2rem' }}>
                <RRAlertCard />
              </div>
              <div style={{ width: '100%', marginBottom: '2rem' }}>
                <RRTVPlayer variant="wide" />
              </div>

                        <div style={{ marginBottom: '4rem' }}>
            <IndexCorrelationLab />

<IndexTopHoldings />

          {/* Separator */}
          <div style={{ margin: '4rem 0', borderTop: `1px dashed ${theme.border}` }} />
<IndexHeroBanner />                 
          {/* REST-based family view: works on weekends, ETF cross-listings + ADR spread */}
          <IndexFamilyRow theme={theme} />

          </div>
              
              {/* ── TESES ── */}
              <SectionDivider number="01" title="RR Surveillance" badge="Narrative Radar" badgeColor="#D4AF37" desc="Banco de hipóteses sob vigilância ativa — agrupadas por sobreposição de componentes para revelar onde narrativas convergem ou competem." href="/thesis" hrefLabel="Banco de teses" />
              <div style={{ marginBottom: '3rem' }}>
                 <HypothesisExplorer theme={theme} />
                {/* <ThesisCenariosBlock pool={marketData?.pool} /> */}
              </div>
<MagmaNewsSection offset={0} />   

              {/* <NewsCardsSection theme={theme} itemsPerRow={3} /> */}

              {/* ── RENDA FIXA ── */}
              <SectionDivider number="06" title="Renda Fixa & Títulos" badge="Juros & Bonds" badgeColor="#22c55e" desc="Curva de juros (Focus), títulos públicos nacionais e bonds internacionais." href="/bonds" hrefLabel="Painel completo" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(350px,1fr))', gap: '1.5rem', marginBottom: '3rem' }}>
               {/* <SelicCard theme={theme} /> */}
                <COPOMOptionsCard theme={theme} />
                <CurvaDeJuros theme={theme} />
              </div>

              {/* ── AGENDA ── */}
              <SectionDivider number="02" title="Agenda da Semana" badge="Resultados & Macro" badgeColor="#D4AF37" desc="Balanços corporativos e indicadores macroeconômicos com impacto no fluxo." href="/calendario" hrefLabel="Calendário completo" />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(400px,1fr))', gap: '2rem', marginBottom: '3rem' }}>
                <div style={{ maxHeight: 450, overflow: 'hidden', maskImage: 'linear-gradient(to bottom,black 80%,transparent 100%)', WebkitMaskImage: 'linear-gradient(to bottom,black 80%,transparent 100%)' }}>
                  <EarningsCalendarCard theme={theme} maxDays={14} />
                </div>
                <div style={{ maxHeight: 450, overflow: 'hidden', maskImage: 'linear-gradient(to bottom,black 80%,transparent 100%)', WebkitMaskImage: 'linear-gradient(to bottom,black 80%,transparent 100%)' }}>
                  <EconomicCalendarCard theme={theme} maxEvents={15} />
                </div>
              </div>

              {/* ── NOTÍCIAS ── */}
              <SectionDivider number="03" title="Acervo Editorial" badge="RR Notícias" badgeColor="#6660fe" />
            
<MagmaNewsSection offset={7} />   
              {/* ── CRIPTO ── */}
              <SectionDivider number="04" title="Blockchain & Cryptoativos" badge="Real-Time Crypto" badgeColor="#007AFF" desc="Sentimento descentralizado via Polymarket e monitoramento de fluxos institucionais." href="/crypto" hrefLabel="Crypto dashboard" />
              <div style={{ marginBottom: '3rem' }}>
                <div style={{ flex: '1 1 100%', marginBottom: '1.5rem' }}>
                  <BitcoinGlobalScaleCard theme={theme} />
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', alignItems: 'stretch' }}>
                  <div className="single-card-view" style={{ flex: '1 1 350px' }}>
                    <style>{`.single-card-view h3{display:none}.single-card-view>div>div>div:not(:first-child){display:none!important}.single-card-view>div>div{grid-template-columns:1fr!important}`}</style>
                    <CryptoCardsSection theme={theme} />
                  </div>
                  <div style={{ flex: '2 1 600px' }}>
                    <CryptoTreemap theme={theme} />
                  </div>
                </div>
              </div>

<MagmaNewsSection offset={14} />  

              {/* ── B3 ── */}
              <SectionDivider number="07" title="Destaques da B3" badge="Alta Volatilidade" badgeColor="#f97316" desc="Maior pressão de compra e venda no pregão de hoje." href="/stocks" hrefLabel="Painel ações" />
              <div style={{ ...layout.grid('auto'), marginBottom: '3rem' }}>
                <AssetCard theme={theme} title="📈 Maiores Altas" items={marketData?.topGainers?.slice(0,5).map(t => ({ name: t.ticker, code: t.name, price: `R$ ${t.ult?.toFixed(2)}`, change: `+${t.var?.toFixed(2)}%`, positive: true })) || []} />
                <AssetCard theme={theme} title="📉 Maiores Baixas" items={marketData?.topLosers?.slice(0,5).map(t => ({ name: t.ticker, code: t.name, price: `R$ ${t.ult?.toFixed(2)}`, change: `${t.var?.toFixed(2)}%`, positive: false })) || []} />
              </div>

              {/* ── CÂMBIO ── */}
              <SectionDivider number="08" title="Câmbio Geopolítico" badge="G7 · BRICS · ASEAN" badgeColor="#22c55e" href="/fx" hrefLabel="Painel FX" />
              <div style={{ maxHeight: 350, overflow: 'hidden', maskImage: 'linear-gradient(to bottom,black 80%,transparent 100%)', WebkitMaskImage: 'linear-gradient(to bottom,black 80%,transparent 100%)', marginBottom: '3rem' }}>
                <CurrencyClusters theme={theme} />
              </div>

              {/* ── ÍNDICES ── */}
              <SectionDivider number="09" title="Índices Globais" href="/indices" hrefLabel="Painel índices" />
              <div style={{ marginBottom: '3rem' }}>
                <IndexClusters theme={theme} />
              </div>

<MagmaNewsSection offset={21} />  

              {/* ── COMMODITIES ── */}
              <SectionDivider number="10" title="Commodities por Setor" badge="Industrial & Agro" badgeColor="#ca8a04" desc="Monitoramento segmentado das cadeias produtivas globais." />
              <div style={{ marginBottom: '3rem' }}>
                <CommodityClusters theme={theme} />
              </div>

            
<MagmaNewsSection offset={28} />  
              {/* ── GIRO ── */}
              <SectionDivider number="11" title="Giro do Mercado" badge="Monitoramento Global" badgeColor="#3b82f6" desc="Visão unificada das principais commodities, moedas e índices em tempo real." />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <MetaisCard theme={theme} limit={6} />
                <CommoditiesCard theme={theme} limit={6} />
                <CurrenciesCard theme={theme} limit={6} />
                <IndicesCard theme={theme} limit={6} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1.5rem', marginBottom: '3rem' }}>
                <MetaisCard theme={theme} />
                <CommoditiesCard theme={theme} />
                <CurrenciesCard theme={theme} />
                <IndicesCard theme={theme} />
              </div>

              <AssetCard theme={theme} title="🔥 Mais Negociados" items={marketData?.mostActive?.slice(0,5).map(t => ({ name: t.ticker, code: t.name, price: `R$ ${t.ult?.toFixed(2)}`, change: `${t.var >= 0 ? '+' : ''}${t.var?.toFixed(2)}%`, positive: t.var >= 0 })) || []} />

              
<MagmaNewsSection offset={35} />  

<MagmaNewsSection offset={43} />  

            </div>
          )}
        </main>

        <footer style={{ background: theme.background, borderTop: `1px solid ${theme.border}`, padding: '2rem 0', marginTop: '3rem', backdropFilter: 'blur(10px)' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src={magmaLogo} alt="RR Magma" style={{ height: 28, width: 'auto', opacity: 0.5 }} />
            </div>
            <p style={{ fontFamily: "'Plus Jakarta Sans',system-ui,sans-serif", fontWeight: 400, color: theme.textSecondary, fontSize: '0.85rem', margin: 0 }}>© 2025 Relatório Reservado.</p>
          </div>
        </footer>
      </div>

      <style>{`
        @keyframes scroll { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes scrollUp0 { 0%{transform:translateY(0)} 100%{transform:translateY(-50%)} }
        @keyframes scrollUp1 { 0%{transform:translateY(0)} 100%{transform:translateY(-50%)} }
        @keyframes scrollUp2 { 0%{transform:translateY(0)} 100%{transform:translateY(-50%)} }
        @keyframes scrollUp3 { 0%{transform:translateY(0)} 100%{transform:translateY(-50%)} }
      `}</style>
    </div>
  );
}

export default MarketOverviewPage;